import { UIIcon } from '../../config/icons';
import { UIInputAdapter } from '../ui/component/adapter';
import { UIButton } from '../ui/component/button';
import { UIValueGroup } from '../ui/component/groupdropdown';
import { UIList } from '../ui/component/list';
import { UIToolbar } from '../ui/component/toolbar';
import { addTooltip } from '../ui/component/tooltip';
import { UITree } from '../ui/component/tree';
import { $el } from '../ui/elements';
import { onKey, UIKey } from '../ui/key';
import { UIMenu } from '../ui/menu';
import { uiStartOrder } from '../ui/order';
import { onInteract } from '../ui/touch';
import { UIClassesSpec, UIPoint, UIValue, UIRelPos, UIRect } from '../ui/types';
import { UIViewSpec, UIView } from '../ui/view';
import { YClipObject, $clip } from '../y/clip';
import { $log } from '../y/debug';
import { $on } from '../y/events';
import { YLanguage } from '../y/i18n';
import { EDBlock, EDBlockSpec, EDNewBlock } from './block';
import { EDFields } from './fields';
import { getTextMenu } from './input';
import { EDSection } from './section';

declare module '../y/clip' {
    export interface YClipObjectTypes {
        'ed-block': any;
        'ed-section': any;
    }
}

declare global {
    interface Element {
        ed_object?: EDObject;
    }
}

export interface EDButtonSpec {
    icon: UIIcon;
    classes?: UIClassesSpec;
    action: (button: HTMLElement) => void;
    position: UIPoint;
    tooltip: string;
}

export interface EDSelectionStyle {
    radius?: number;
    thickness?: number;
    offset?: number;
}

export interface EDObject {
    name: string;
    parent: EDObject | undefined;
    properties: UIViewSpec | undefined;
    children?: EDObject[];
    auto_expand?: boolean;
    has_selection?: boolean;
    selection_style?: EDSelectionStyle;
    hover_style?: EDSelectionStyle;
    getButtons?(): EDButtonSpec[];
    ui_elem: HTMLElement;
    doRemove?(): void;
    doCopy?(): void;
    doPaste?(object: YClipObject): void;
}

export interface EDBlockContainer<Extra extends {} = {}> extends EDObject {
    editor: EDEditor<any, any, Extra> & Extra;
    doPaste(object: YClipObject, editor_block?: EDBlock<Extra>): void;
    getChildButtons(block: EDBlock<Extra>): EDButtonSpec[];
    removeBlock?(block: EDBlock<Extra>): void;
    insertBlock?(rel: EDBlock<Extra> | undefined, anchor: UIViewSpec): void;
}

const language_values: UIValue<YLanguage>[] = [{ key: 'en', name: 'EN' }, { key: 'sv', name: 'SV' }, { key: 'de', name: 'DE' }];

function makeEdButton(spec: EDButtonSpec, content_rect: DOMRect): UIViewSpec {
    const button = $el.DIV(['ed-button', spec.classes], $el.ICON(spec.icon));

    button.addEventListener('click', e => {
        spec.action(button);
        e.stopPropagation();
        e.preventDefault();
    });

    button.style.left = (spec.position[0] - content_rect.left) + 'px';
    button.style.top = (spec.position[1] - content_rect.top) + 'px';

    addTooltip(spec.tooltip, button, { position: UIRelPos.Above, offset: 10 });
    return button;
}

const legacy_block: EDBlockSpec<any, any> = {
    key: 'legacy',
    editor_block: class extends EDBlock<any> {
        constructor(container: EDBlockContainer, type: string) {
            super(undefined, container, `Unknown (${type})`);
            this.ui_add([$el.DIV('s-small'), $el.P('subtitle', 'This block is not supported in the new editor. Please remove it and add a new block.', 'p-a-center'), $el.DIV('s-small')]);
        }
    }
}

export abstract class EDEditor<Section, Block extends { type: string }, Extra extends {} = {}> extends UIView implements EDObject {
    toolbar: UIToolbar;
    sections: UIInputAdapter<Section, EDSection<Section, Block, Extra>>;
    list: UIList;
    protected content: HTMLElement;
    fields: EDFields;
    tree: UITree<EDObject>;
    properties: UIViewSpec | undefined;
    properties_container: HTMLElement;
    name: string;
    parent: undefined;
    auto_expand: boolean;
    block_specs: { [key in Block['type']]?: EDBlockSpec<Block, Extra> };
    new_blocks: UIValueGroup<EDNewBlock<Block>>[];
    pre_objects: EDObject[];
    post_objects: EDObject[];
    translate_mode?: boolean;

    _observer: ResizeObserver;
    _observing?: HTMLElement;
    _observed_rect?: UIRect;
    _ed_buttons: HTMLElement[]
    _selection_display: HTMLElement;
    _hover_display: HTMLElement;

    private _zoomed_out?: boolean;
    private _selected: EDObject;
    private _form_container: HTMLElement;
    private _selected_name: HTMLElement;

    constructor(fields: EDFields, block_specs: EDBlockSpec<Block, Extra>[], new_blocks: UIValueGroup<EDNewBlock<Block>>[], name: string, languages: YLanguage[], classes?: UIClassesSpec, translate_mode?: boolean) {
        let language: YLanguage = languages.includes('en') ? 'en' : languages.includes('sv') ? 'sv' : languages.includes('de') ? 'de' : 'en';

        const setLanguage = (l: YLanguage) => {
            language = l;
            this.root.ui_set('ui-edit-language', l);
        };

        super('div', ['ed-editor', classes, translate_mode && 'm-translate-mode']);
        this._selected = this;
        this.translate_mode = translate_mode;

        this.auto_expand = true;
        this.name = name;
        this.fields = fields;
        this.block_specs = {};
        for(const s of block_specs) {
            this.block_specs[s.key] = s;
        }
        this.new_blocks = new_blocks;

        this.pre_objects = [];
        this.post_objects = [];

        const remove_button = new UIButton({
            icon: UIIcon.TRASH_CAN, on_click: () => {
                this._selected.doRemove?.();
                this.deselectObject(this._selected);
                this.properties_container.ui_hide();
            }
        });

        const paste_button = new UIButton({
            icon: UIIcon.PASTE, on_click: () => {
                const selected = this._selected;
                if(selected?.doPaste)
                    $clip.readObject().then(object => { object && selected.doPaste!(object) });
            }
        });

        const copy_button = new UIButton({
            icon: UIIcon.COPY, on_click: () => {
                this._selected?.doCopy?.();
            }
        });

        addTooltip('Delete', remove_button, { offset: 4, hover: remove_button, position: UIRelPos.Above });
        addTooltip('Paste', paste_button, { offset: 4, hover: paste_button, position: UIRelPos.Above });
        addTooltip('Copy', copy_button, { offset: 4, hover: copy_button, position: UIRelPos.Above });

        this.properties_container = $el.DIV('l-form', [
            this._selected_name = $el.DIV('r-name'),
            $el.DIV('l-button-row', [remove_button, paste_button, copy_button]),
            this._form_container = $el.DIV()
        ]);
        this.properties_container.ui_hide();

        const sections = this.sections = new UIInputAdapter((_, e) => {
            const section = this.makeSection();
            onInteract(section.order_handle, e => uiStartOrder(e, list.onOrder(section)));
            section.doRemove = () => { sections.remove(e); this.deselectObject(section); };
            return { view: section, input: section.form };
        });

        const list = this.list = new UIList(sections, { classes: 'ed-section-list' });

        this._ed_buttons = [];
        const content = $el.DIV('r-content ed-object', [
            list,
            this._hover_display = $el.DIV('ed-hover-display'),
            this._selection_display = $el.DIV('ed-selection-display'),
        ]);
        content.ed_object = this;

        this.ui_add([
            this.content = content,
        ]);

        this._hover_display.ui_hide();
        this._selection_display.ui_hide();

        this.tree = new UITree<EDObject>({
            getRoot: () => this,
            hasChildren: (v: EDObject) => !!v.children?.length,
            getChildren: (v: EDObject) => v.children || [],
            getLabel: (v: EDObject) => v.name,
            getAutoExpand: (v: EDObject) => !!v.auto_expand,
            select: (v: EDObject) => this.selectObject(v, true),
            isSelected: (v: EDObject) => this._selected == v,
            hasSelected: (v: EDObject) => !!v.has_selection,
        }, { expand_root: true });

        $on(sections, 'change', () => {
            this.tree.update();
        })

        this.toolbar = new UIToolbar(new UIMenu([
            { select: language_values.filter(k => languages.includes(k.key)), get value() { return language }, set value(l) { setLanguage(l) } },
            {
                title: 'Copy', icon: UIIcon.COPY, submenu: new UIMenu([
                    { header: 'Copy from' },
                    { separator: true },
                    { title: 'English', action: () => { this.current_object?.ui_trigger('ui-copy-language', ['en', language]) }, when: () => language != 'en' && languages.includes('en') },
                    { title: 'Swedish', action: () => { this.current_object?.ui_trigger('ui-copy-language', ['sv', language]) }, when: () => language != 'sv' && languages.includes('sv') },
                    { title: 'German', action: () => { this.current_object?.ui_trigger('ui-copy-language', ['de', language]) }, when: () => language != 'de' && languages.includes('de') }
                ])
            },
            {
                title: 'Translate', icon: UIIcon.WAND_MAGIC_SPARKLES, submenu: new UIMenu([
                    { header: 'Translate from' },
                    { separator: true },
                    { title: 'English', action: () => { this.current_object?.ui_trigger('ui-translate-language', ['en', language]) }, when: () => language != 'en' && languages.includes('en') },
                    { title: 'Swedish', action: () => { this.current_object?.ui_trigger('ui-translate-language', ['sv', language]) }, when: () => language != 'sv' && languages.includes('sv') },
                    { title: 'German', action: () => { this.current_object?.ui_trigger('ui-translate-language', ['de', language]) }, when: () => language != 'de' && languages.includes('de') }
                ])
            },
            { separator: true, when: block_specs != undefined },
            {
                title: 'Add section', icon: UIIcon.CIRCLE_PLUS, action: () => {
                    const current = this._selected?.ui_elem.closest('.ed-section')?.ed_object;
                    const entry = current && this.sections.findView(current as EDSection<Section, Block, Extra>);
                    const new_entry = current ? this.sections.insertAfter(this.newSection(), entry) : this.sections.push(this.newSection());
                    this.selectObject(new_entry?.view, true);
                }, when: block_specs != undefined && !translate_mode
            },
            { title: 'Zoom', icon: UIIcon.MAGNIFYING_GLASS_PLUS, action: () => { this.zoomOut() }, when: block_specs != undefined && !translate_mode },
            { separator: true },
            { inline: getTextMenu() }
        ]));

        setLanguage(language);

        this._observer = new ResizeObserver(entries => {
            $log('app', 'resize', entries);
            for(const e of entries) {
                if(e.target == this._selected?.ui_elem) {
                    const rect = e.target.getBoundingClientRect();
                    const observed = this._observed_rect;
                    if(observed && rect.left == observed[0] && rect.top == observed[1] && rect.width == observed[2] && rect.height == observed[3]) {
                        $log('app', 'resize no change');
                        continue;
                    }
                    this._showSelected(this._selected, rect);
                }
            }
        });


        if(!translate_mode) {
            let last: EDObject | undefined;

            content.addEventListener('mousemove', e => {
                let object = (e.target as HTMLElement)?.closest?.('.ed-object')?.ed_object;
                if(object == this)
                    object = undefined;

                if(last != object) {
                    this._showHover(object);
                    last = object;
                }
            });
        }

        this.ui_elem.tabIndex = 0;

        onKey(this, e => {
            if(e.key == UIKey.Escape) {
                this.selectObject(this);
            }
        })
    }

    private _showSelected(object: EDObject, rect?: DOMRect) {
        this._hover_display.ui_hide();

        const elem = (object == this || this._zoomed_out) ? undefined : object.ui_elem;
        if(elem != this._observing) {
            if(this._observing)
                this._observer.unobserve(this._observing);
            if(elem)
                this._observer.observe(elem);
            this._observing = elem;
        }

        for(const b of this._ed_buttons) {
            b.remove();
        }

        if(!elem) {
            this._selection_display.ui_hide();
            return;
        }

        rect ??= elem.getBoundingClientRect();
        this._observed_rect = [rect.left, rect.top, rect.width, rect.height];
        const content_rect = this.content.getBoundingClientRect();
        const selection_style = object.selection_style || {};
        const offset = selection_style.offset ?? 8;

        $log('app', 'selection style', selection_style);

        // this._hover_display.ui_setText(object.name);
        const top = Math.max(0, rect.top - content_rect.top - offset);
        const left = Math.max(0, rect.left - content_rect.left - offset);
        const right = Math.min(content_rect.width, rect.right - content_rect.left + offset);
        const bottom = Math.min(content_rect.height, rect.bottom - content_rect.top + offset);

        // important to get buttons before so we don't modify the DOM
        const button_specs = object.getButtons?.() || [];

        this.content.ui_add(this._ed_buttons = button_specs.map(b => makeEdButton(b, content_rect) as HTMLElement));

        const radius = selection_style.radius ?? 10;
        const thickness = selection_style.thickness ?? 3;

        this._selection_display.style.setProperty('--ed-radius', radius + 'px');
        this._selection_display.style.setProperty('--ed-thickness', thickness + 'px');
        this._selection_display.style.top = top + 'px';
        this._selection_display.style.left = left + 'px';
        this._selection_display.style.width = (right - left) + 'px';
        this._selection_display.style.height = (bottom - top) + 'px';
        this._selection_display.ui_show();
    }

    _showHover(object: EDObject | undefined) {
        if(!object || object == this._selected || this._zoomed_out) {
            this._hover_display.ui_hide();
            return;
        }

        const rect = object.ui_elem.getBoundingClientRect();
        const content_rect = this.content.getBoundingClientRect();
        // this._hover_display.ui_setText(object.name);
        const hover_style = object.hover_style || {};
        const offset = hover_style.offset ?? 4;

        const top = Math.max(0, rect.top - content_rect.top - offset);
        const left = Math.max(0, rect.left - content_rect.left - offset);
        const right = Math.min(content_rect.width, rect.right - content_rect.left + offset);
        const bottom = Math.min(content_rect.height, rect.bottom - content_rect.top + offset);

        const radius = hover_style.radius ?? 4;
        const thickness = hover_style.thickness ?? 2;

        this._hover_display.style.setProperty('--ed-radius', radius + 'px');
        this._hover_display.style.setProperty('--ed-thickness', thickness + 'px');
        this._hover_display.style.top = top + 'px';
        this._hover_display.style.left = left + 'px';
        this._hover_display.style.width = (right - left) + 'px';
        this._hover_display.style.height = (bottom - top) + 'px';
        this._hover_display.ui_show();
    }

    selectObject(object?: EDObject, scroll?: boolean) {
        if(this.translate_mode) return;
        object ??= this;
        if(this._selected == object) return;
        $log('editor', 'selected object', object);
        if(this._selected) {
            for(let parent = this._selected.parent; parent; parent = parent.parent) {
                parent.has_selection = false;
            }
        }

        this._selected = object;
        this._showSelected(object);
        for(let parent = this._selected.parent; parent; parent = parent.parent) {
            parent.has_selection = true;
        }

        this._selected_name.textContent = object.name;

        this.properties_container.ui_show();
        this._form_container.ui_empty([object.properties]);

        $log('app', 'items', this.children);

        this.tree.update();

        if(scroll)
            object.ui_elem.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    deselectObject(object?: EDObject) {
        if(this._selected == object) {
            this.selectObject(this);
        }
    }

    zoomIn() {
        const content_elem = this.content;
        this._zoomed_out = false;

        content_elem.style.scale = '';

        this.classList.remove('f-zoom-out');
        this.list.classList.remove('f-zoom-out');
    }

    zoomOut() {
        if(this._zoomed_out) {
            this.zoomIn();
            return;
        }

        this._zoomed_out = true;
        this._showHover(undefined);
        this._showSelected(this._selected);

        const root_elem = this.root;
        const content_elem = this.content;
        const height = root_elem.offsetHeight;
        const scroll_height = root_elem.scrollHeight;
        const scale = height / scroll_height;
        const on_top = root_elem.scrollTop;
        const on_bottom = scroll_height - height - on_top;
        const y_offset = (on_top + on_bottom > 0) ? on_top * (height + on_top + on_bottom) / (on_top + on_bottom) : 0;

        root_elem.style.setProperty('--ed-scrollbar-width', `${root_elem.offsetWidth - root_elem.clientWidth}px`);
        root_elem.classList.add('f-zoom-out');
        this.list.classList.add('f-zoom-out');
        content_elem.style.transformOrigin = `50% ${y_offset}px`;
        content_elem.style.scale = scale + '';
    }

    get current_object(): HTMLElement | undefined {
        return this._selected?.ui_elem;
    }

    get children(): EDObject[] {
        return [...this.pre_objects, ...this.sections.views, ...this.post_objects];
    }

    doPaste(object: YClipObject, editor_section?: EDSection<Section, Block, Extra>): void {
        if(object[0] == 'ed-section') {
            const sections = this.sections;
            const entry = sections.insertBefore(object[1], editor_section && sections.findView(editor_section));
            this.selectObject(entry?.view);
        }
    }

    makeBlock(b: Block, container: EDBlockContainer<Extra>): EDBlock<any> {
        const spec = this.block_specs[b.type as Block['type']] ?? legacy_block;
        return new spec.editor_block(container, b.type);
    }

    protected abstract makeSection(): EDSection<Section, Block, Extra>;
    protected abstract newSection(): Section;
}
